import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Calendar,
  MapPin,
  Users,
  Clock,
  CreditCard,
  CheckCircle,
  XCircle,
  Loader2,
  Mail,
  Ticket,
  ArrowRight,
  Lock,
  KeyRound,
  User,
  LogOut,
  Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyReservationsPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);
  const [authMethod, setAuthMethod] = useState("code"); // "code" or "login"
  
  // Code verification state
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  
  // Login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  
  // Register state
  const [showRegister, setShowRegister] = useState(false);
  const [registerData, setRegisterData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
  });
  const [registering, setRegistering] = useState(false);
  
  // Reservations state
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem("client_token");
    const client = localStorage.getItem("client_info");
    if (token && client) {
      setIsAuthenticated(true);
      setClientInfo(JSON.parse(client));
      fetchReservations(JSON.parse(client).email);
    }
  }, []);

  const texts = {
    title: { fr: "Mes Réservations", en: "My Reservations" },
    subtitle: { fr: "Accédez à vos réservations de manière sécurisée", en: "Access your reservations securely" },
    codeTab: { fr: "Code par email", en: "Email code" },
    loginTab: { fr: "Connexion", en: "Login" },
    email: { fr: "Adresse email", en: "Email address" },
    sendCode: { fr: "Recevoir le code", en: "Get code" },
    codeSent: { fr: "Code envoyé ! Vérifiez votre boîte mail", en: "Code sent! Check your email" },
    enterCode: { fr: "Entrez le code reçu", en: "Enter the code received" },
    verify: { fr: "Vérifier", en: "Verify" },
    password: { fr: "Mot de passe", en: "Password" },
    login: { fr: "Se connecter", en: "Login" },
    noAccount: { fr: "Pas de compte ?", en: "No account?" },
    createAccount: { fr: "Créer un compte", en: "Create account" },
    register: { fr: "Inscription", en: "Register" },
    name: { fr: "Nom complet", en: "Full name" },
    phone: { fr: "Téléphone", en: "Phone" },
    confirmPassword: { fr: "Confirmer le mot de passe", en: "Confirm password" },
    haveAccount: { fr: "Déjà un compte ?", en: "Already have an account?" },
    logout: { fr: "Déconnexion", en: "Logout" },
    welcome: { fr: "Bienvenue", en: "Welcome" },
    noResults: { fr: "Aucune réservation trouvée", en: "No reservations found" },
    status: {
      pending: { fr: "En attente", en: "Pending" },
      confirmed: { fr: "Confirmée", en: "Confirmed" },
      cancelled: { fr: "Annulée", en: "Cancelled" },
      completed: { fr: "Terminée", en: "Completed" },
    },
    paymentStatus: {
      unpaid: { fr: "Non payé", en: "Unpaid" },
      paid: { fr: "Payé", en: "Paid" },
      refunded: { fr: "Remboursé", en: "Refunded" },
    },
    travelers: { fr: "voyageurs", en: "travelers" },
    participants: { fr: "participants", en: "participants" },
    days: { fr: "jours", en: "days" },
    circuit: { fr: "Circuit", en: "Circuit" },
    stage: { fr: "Stage", en: "Retreat" },
    pay: { fr: "Payer maintenant", en: "Pay now" },
  };

  const t = (key) => texts[key]?.[lang] || texts[key]?.fr || key;

  const handleSendCode = async () => {
    if (!email) {
      toast.error(lang === "fr" ? "Veuillez entrer votre email" : "Please enter your email");
      return;
    }

    setSendingCode(true);
    try {
      await axios.post(`${API}/client/send-verification`, { email });
      setCodeSent(true);
      toast.success(t("codeSent"));
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur";
      toast.error(msg);
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      toast.error(lang === "fr" ? "Veuillez entrer le code" : "Please enter the code");
      return;
    }

    setVerifying(true);
    try {
      const response = await axios.post(`${API}/client/verify-code`, {
        email,
        code: verificationCode,
      });
      
      localStorage.setItem("client_token", response.data.token);
      localStorage.setItem("client_info", JSON.stringify({ email: response.data.email, name: "Invité" }));
      setIsAuthenticated(true);
      setClientInfo({ email: response.data.email, name: "Invité" });
      fetchReservations(response.data.email);
      toast.success(lang === "fr" ? "Vérification réussie" : "Verification successful");
    } catch (error) {
      const msg = error.response?.data?.detail || "Code incorrect";
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const response = await axios.post(`${API}/client/login`, {
        email: loginEmail,
        password: loginPassword,
      });
      
      localStorage.setItem("client_token", response.data.token);
      localStorage.setItem("client_info", JSON.stringify(response.data.client));
      setIsAuthenticated(true);
      setClientInfo(response.data.client);
      fetchReservations(response.data.client.email);
      toast.success(lang === "fr" ? "Connexion réussie" : "Login successful");
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur de connexion";
      toast.error(msg);
    } finally {
      setLoggingIn(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) {
      toast.error(lang === "fr" ? "Les mots de passe ne correspondent pas" : "Passwords don't match");
      return;
    }

    setRegistering(true);
    try {
      await axios.post(`${API}/client/register`, {
        name: registerData.name,
        email: registerData.email,
        password: registerData.password,
        phone: registerData.phone,
      });
      
      toast.success(lang === "fr" ? "Compte créé ! Vous pouvez maintenant vous connecter" : "Account created! You can now login");
      setShowRegister(false);
      setLoginEmail(registerData.email);
      setAuthMethod("login");
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur lors de l'inscription";
      toast.error(msg);
    } finally {
      setRegistering(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("client_token");
    localStorage.removeItem("client_info");
    setIsAuthenticated(false);
    setClientInfo(null);
    setReservations([]);
    setCodeSent(false);
    setVerificationCode("");
    setEmail("");
  };

  const fetchReservations = async (userEmail) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/my-reservations`, { email: userEmail });
      setReservations(response.data);
    } catch (error) {
      toast.error(lang === "fr" ? "Erreur lors du chargement" : "Loading error");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (reservation) => {
    try {
      const endpoint = reservation.type === "stage"
        ? `${API}/stage-reservations/${reservation.id}/checkout`
        : `${API}/reservations/${reservation.id}/checkout`;
      
      const response = await axios.post(endpoint);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(lang === "fr" ? "Erreur lors du paiement" : "Payment error");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed": return "bg-green-100 text-green-700";
      case "pending": return "bg-amber-100 text-amber-700";
      case "cancelled": return "bg-red-100 text-red-700";
      case "completed": return "bg-blue-100 text-blue-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const getPaymentColor = (status) => {
    switch (status) {
      case "paid": return "bg-green-500 text-white";
      case "unpaid": return "bg-red-100 text-red-700";
      case "refunded": return "bg-purple-100 text-purple-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  // Authenticated view - show reservations
  if (isAuthenticated) {
    return (
      <div className="min-h-screen bg-muted/30 py-12" data-testid="my-reservations-page">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("welcome")}</p>
                <h1 className="font-serif text-2xl font-bold">{clientInfo?.name || clientInfo?.email}</h1>
              </div>
            </div>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              {t("logout")}
            </Button>
          </div>

          {/* Reservations */}
          <h2 className="font-serif text-xl font-semibold mb-4">{t("title")}</h2>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : reservations.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <XCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">{t("noResults")}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {reservations.map((reservation) => {
                const item = reservation.circuit || reservation.stage;
                const isStage = reservation.type === "stage";
                const title = item?.title?.[lang] || item?.title?.fr || "Réservation";

                return (
                  <Card key={reservation.id} className="overflow-hidden" data-testid={`reservation-${reservation.id}`}>
                    <div className="flex flex-col md:flex-row">
                      {item?.image_url && (
                        <div className="md:w-48 h-40 md:h-auto">
                          <img src={item.image_url} alt={title} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="flex-1 p-6">
                        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline">
                                {isStage ? t("stage") : t("circuit")}
                              </Badge>
                              <Badge className={getStatusColor(reservation.status)}>
                                {texts.status[reservation.status]?.[lang] || reservation.status}
                              </Badge>
                              <Badge className={getPaymentColor(reservation.payment_status)}>
                                {reservation.payment_status === "paid" && <CheckCircle className="h-3 w-3 mr-1" />}
                                {texts.paymentStatus[reservation.payment_status]?.[lang] || reservation.payment_status}
                              </Badge>
                            </div>
                            <h3 className="font-serif text-xl font-semibold">{title}</h3>
                          </div>
                          <span className="text-2xl font-bold text-primary">{reservation.total_price}€</span>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            {formatDate(reservation.created_at)}
                          </div>
                          {item?.duration_days && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Clock className="h-4 w-4" />
                              {item.duration_days} {t("days")}
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Users className="h-4 w-4" />
                            {reservation.num_travelers || reservation.num_participants}{" "}
                            {isStage ? t("participants") : t("travelers")}
                          </div>
                          {item?.departure_city && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <MapPin className="h-4 w-4" />
                              {item.departure_city}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t">
                          <div className="text-sm text-muted-foreground">
                            <span className="font-medium">ID:</span> {reservation.id.slice(0, 8)}...
                          </div>
                          {reservation.payment_status === "unpaid" && (
                            <Button size="sm" onClick={() => handlePayment(reservation)}>
                              <CreditCard className="h-4 w-4 mr-2" />
                              {t("pay")}
                              <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Registration form
  if (showRegister) {
    return (
      <div className="min-h-screen bg-muted/30 py-12" data-testid="register-page">
        <div className="max-w-md mx-auto px-4">
          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="font-serif text-2xl">{t("register")}</CardTitle>
              <CardDescription>Créez votre compte client</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label>{t("name")} *</Label>
                  <Input
                    value={registerData.name}
                    onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>{t("email")} *</Label>
                  <Input
                    type="email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>{t("phone")}</Label>
                  <Input
                    value={registerData.phone}
                    onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>{t("password")} *</Label>
                  <Input
                    type="password"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    minLength={6}
                    required
                  />
                </div>
                <div>
                  <Label>{t("confirmPassword")} *</Label>
                  <Input
                    type="password"
                    value={registerData.confirmPassword}
                    onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                    minLength={6}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={registering}>
                  {registering && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t("register")}
                </Button>
                <p className="text-center text-sm text-muted-foreground">
                  {t("haveAccount")}{" "}
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => setShowRegister(false)}
                  >
                    {t("login")}
                  </button>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Login/Verification view
  return (
    <div className="min-h-screen bg-muted/30 py-12" data-testid="my-reservations-auth">
      <div className="max-w-md mx-auto px-4">
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
            <Shield className="h-10 w-10 text-primary" />
          </div>
          <h1 className="font-serif text-3xl font-bold text-secondary mb-2">{t("title")}</h1>
          <p className="text-muted-foreground">{t("subtitle")}</p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <Tabs value={authMethod} onValueChange={setAuthMethod}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="code" className="gap-2">
                  <KeyRound className="h-4 w-4" />
                  {t("codeTab")}
                </TabsTrigger>
                <TabsTrigger value="login" className="gap-2">
                  <Lock className="h-4 w-4" />
                  {t("loginTab")}
                </TabsTrigger>
              </TabsList>

              {/* Code Verification Tab */}
              <TabsContent value="code" className="space-y-4">
                {!codeSent ? (
                  <>
                    <div>
                      <Label className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        {t("email")} *
                      </Label>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="votre@email.com"
                        className="mt-2"
                      />
                    </div>
                    <Button
                      className="w-full"
                      onClick={handleSendCode}
                      disabled={sendingCode}
                    >
                      {sendingCode ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Mail className="h-4 w-4 mr-2" />
                      )}
                      {t("sendCode")}
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm text-center">
                      <CheckCircle className="h-4 w-4 inline mr-2" />
                      {t("codeSent")}
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <KeyRound className="h-4 w-4" />
                        {t("enterCode")}
                      </Label>
                      <Input
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        placeholder="123456"
                        maxLength={6}
                        className="mt-2 text-center text-2xl tracking-widest"
                      />
                    </div>
                    <Button
                      className="w-full"
                      onClick={handleVerifyCode}
                      disabled={verifying}
                    >
                      {verifying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      {t("verify")}
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full"
                      onClick={() => { setCodeSent(false); setVerificationCode(""); }}
                    >
                      Changer d'email
                    </Button>
                  </>
                )}
              </TabsContent>

              {/* Login Tab */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {t("email")} *
                    </Label>
                    <Input
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Lock className="h-4 w-4" />
                      {t("password")} *
                    </Label>
                    <Input
                      type="password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      className="mt-2"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loggingIn}>
                    {loggingIn && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {t("login")}
                  </Button>
                  <p className="text-center text-sm text-muted-foreground">
                    {t("noAccount")}{" "}
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() => setShowRegister(true)}
                    >
                      {t("createAccount")}
                    </button>
                  </p>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MyReservationsPage;
